<script>
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { format } from 'svelte-i18n';
  import { Col, Input, Row } from '@sveltestrap/sveltestrap';

  import { CardEditor, CardPreview, CardPreviewMobile } from '@client/components/bizcard-editor';
  import { getNotificationsContext } from '@client/components/notifications';
  import { Section, SectionItem } from '@client/components/section';
  import { Select } from '@client/components/select';
  import { Step } from '@client/components/steps';
  import { UserSelect } from '@client/components/user-select';

  import { isLeafNode, parseCssVariables } from '@client/pages/bizcards/studio/designer/utils';
  import VariableGroup from '@client/pages/bizcards/studio/designer/variable-group.svelte';

  import { appStore } from '@client/stores/app';
  import {
    id,
    renderedTemplate,
    selectedUser as bizcardSelectedUser,
    setSelectedUser,
    template as bizcardTemplate,
    updatedRulename,
    updatePreview,
    updateVariable,
    variables
  } from '@client/stores/bizcard';
  import { userStore } from '@client/stores/user';

  import { sanitizeRulenameOnKeyUp } from '@client/utils/sanitize';

  const { addNotification } = getNotificationsContext();

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  /**
   * Operation mode for the studio form ('create' or 'update').
   * @type {string}
   * @default 'create'
   */
  export let mode = 'create';

  /**
   * Title displayed in the first step of the form.
   * @type {string}
   * @default 'Select Template'
   */
  export let firstStepTitle = 'Select Template';

  /**
   * Title displayed in the second step of the form.
   * @type {string}
   * @default 'Configure Business Card'
   */
  export let secondStepTitle = 'Configure Business Card';

  /**
   * Label for the execute/submit button (i18n key).
   * @type {string}
   * @default 'label.CREATE_BUSINESS_CARD'
   */
  export let executeLabel = 'label.CREATE_BUSINESS_CARD';

  /**
   * Currently selected item (template or business card).
   * @type {Object | null}
   * @default null
   */
  export let selectedItem = null;

  /**
   * Async function to load the selected template or business card.
   * @type {Function | null}
   * @default null
   */
  export let onLoadItem = null;

  /**
   * Async function to execute the save or update operation.
   * @type {Function | null}
   * @default null
   */
  export let onExecuteAction = null;

  /**
   * Async function to rollback the operation.
   * @type {Function | null}
   * @default null
   */
  export let onRollbackAction = null;

  let currentStep = 0;
  let configurationComplete = false;
  let users = [];
  let userSelectValue = null;
  let updatingPreview = false;
  let selectedElement = null;
  let stepNextDisabled = true;
  let editMode = { value: 'visual', label: 'Visual Edit' };
  let originalSource = '';
  let loadedItemId = null; // Track which item we've loaded to prevent loops

  const editModeOptions = [
    { value: 'visual', label: 'Visual Edit' },
    { value: 'html', label: 'Edit HTML' }
  ];

  const steps = [
    {
      title: firstStepTitle,
      component: 'selection'
    },
    {
      title: secondStepTitle,
      component: 'configuration'
    }
  ];

  const handleUserChange = (event) => {
    userSelectValue = event.detail;
    setSelectedUser(event.detail);
  };

  const handleCardMessage = (e) => {
    if (e.data?.source === 'card' && currentStep === 1) {
      const { target } = e.data;
      if (target) {
        selectedElement = target;
      }
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      currentStep = 1;
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      currentStep = 0;
    }
  };

  const handleStepClick = (index) => {
    if (index === 0) {
      currentStep = 0;
    }
    if (index === 1 && selectedItem) {
      currentStep = 1;
    }
  };

  const executeStudioAction = async () => {
    $appStore.loading = true;

    try {
      const result = await onExecuteAction();

      $appStore.loading = false;

      addNotification({
        text: $format('label.SUCCESS.BUSINESS_CARD_UPDATE'),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });

      return result;
    } catch (error) {
      $appStore.loading = false;

      addNotification({
        text: $format('label.FAILURE.BUSINESS_CARD_UPDATE'),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });

      throw error;
    }
  };

  const rollbackStudioAction = async () => {
    $appStore.loading = true;

    try {
      await onRollbackAction();
    } catch (error) {
      console.error('Failed to rollback bizcard action:', error);
    } finally {
      $appStore.loading = false;
    }
  };

  onMount(() => {
    if (onExecute) {
      onExecute(executeStudioAction);
    }
    if (onRollback) {
      onRollback(rollbackStudioAction);
    }
  });

  // When selectedItem changes, load it and update stores
  $: if (selectedItem) {
    const itemId = selectedItem.id || selectedItem.value || selectedItem.name;

    // Only load if this is a different item than what we've already loaded
    if (itemId !== loadedItemId) {
      loadedItemId = itemId;

      (async () => {
        if (onLoadItem) {
          await onLoadItem(selectedItem);
        }

        // Save original source and set updatedRulename
        originalSource = get(bizcardTemplate);
        $updatedRulename = $id;

        // Ensure preview updates with hover borders
        await updatePreview();
      })();
    }
  } else {
    // Reset when item is cleared
    loadedItemId = null;
  }

  $: configurationComplete = currentStep === 1;
  $: if ($renderedTemplate && currentStep === 1) {
    updatingPreview = true;
  }
  $: variableTree = Object.keys($variables).length ? parseCssVariables($variables) : {};
  $: selectedElementVariables = selectedElement && variableTree[selectedElement] ? variableTree[selectedElement] : null;
  $: if (users.length) {
    const userInDomain = users.some((user) => user.value === $bizcardSelectedUser?.value);

    if (!userInDomain) {
      setSelectedUser(null);
      userSelectValue = null;
    }

    if (!userSelectValue) {
      if (!$bizcardSelectedUser) {
        const userToSelect = $userStore.isManaging ? users[0] : $userStore.currentUser;
        setSelectedUser(userToSelect);
      }

      userSelectValue = $bizcardSelectedUser;
    }
  }

  $: nextLabel = currentStep === 0 ? 'label.NEXT' : executeLabel;
  $: stepSummary = currentStep === 0 && selectedItem ? selectedItem.label || selectedItem.name : undefined;
  $: stepNextDisabled = currentStep === 0 && !selectedItem;
  $: nextDisabled = !configurationComplete;
</script>

<div class="bizcard-studio-form">
  <Step
    active={currentStep === 0}
    disabled={false}
    nextDisabled={stepNextDisabled}
    title={steps[0].title}
    summary={stepSummary}
    onNext={handleNext}
    onStep={() => handleStepClick(0)}
  >
    <div class="selection-wrapper">
      <slot name="selection" />
    </div>
  </Step>

  <Step
    active={currentStep === 1}
    disabled={!selectedItem}
    title={steps[1].title}
    onBack={handleBack}
    onStep={() => handleStepClick(1)}
  >
    {#if selectedItem}
      <div class="configuration-content">
        <Section>
          <SectionItem
            label="Business Card Name"
            description="Enter a unique name for this business card template"
            htmlFor="bizcardName"
          >
            <Input
              id="bizcardName"
              type="text"
              bind:value={$updatedRulename}
              on:keyup={() => ($updatedRulename = sanitizeRulenameOnKeyUp($updatedRulename))}
              placeholder="Enter business card name"
            />
          </SectionItem>

          <SectionItem label="Preview User" description="Select a user to preview the business card">
            <UserSelect
              bind:users
              value={userSelectValue}
              on:change={handleUserChange}
              isClearable={false}
              showChevron
            />
          </SectionItem>

          <SectionItem label="Edit Mode" description="Choose how to edit the business card">
            <Select items={editModeOptions} bind:value={editMode} clearable={false} searchable={false} />
          </SectionItem>
        </Section>

        <div class="card-editor">
          <Row>
            {#if editMode?.value === 'visual'}
              <Col md="6">
                <div class="preview-section">
                  <div class="preview-container" class:d-none={updatingPreview}>
                    <CardPreviewMobile>
                      <CardPreview source={$renderedTemplate} on:load={() => (updatingPreview = false)} />
                    </CardPreviewMobile>
                  </div>
                </div>
              </Col>
              <Col md="6">
                {#if selectedElement && selectedElementVariables}
                  <div class="edit-controls">
                    <h5 class="mb-3 text-capitalize">{selectedElement.replace(/_/g, ' ')}</h5>
                    <div class="variables-editor">
                      <VariableGroup
                        nodeData={selectedElementVariables}
                        nodeKey={selectedElement}
                        level={2}
                        onChange={updateVariable}
                      />
                    </div>
                  </div>
                {:else if selectedElement}
                  <div class="edit-controls">
                    <h5 class="mb-3 text-capitalize">{selectedElement.replace(/_/g, ' ')}</h5>
                    <p class="text-muted">No editable properties found for this element.</p>
                  </div>
                {:else}
                  <div class="edit-controls-placeholder">
                    <p class="text-muted text-center py-4">Click on an element in the preview to edit it</p>
                  </div>
                {/if}
              </Col>
            {:else}
              <div class="edit-controls-html">
                <CardEditor bind:source={$bizcardTemplate} {originalSource} />
              </div>
            {/if}
          </Row>
        </div>
      </div>
    {/if}
  </Step>
</div>

<svelte:window on:message={handleCardMessage} />

<style lang="scss">
  .selection-wrapper {
    :global {
      .section {
        &:first-child {
          margin-top: 0 !important;
        }
      }
    }
  }

  .card-editor {
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-large);
    margin-top: var(--margin-large);
    padding: var(--padding-xlarge);
  }

  .preview-section {
    height: 560px;

    .preview-container {
      transform: scale(0.8);
      transform-origin: top center;
      width: fit-content;

      :global(.outer) {
        margin-top: 0 !important;
      }
    }
  }

  .edit-controls,
  .edit-controls-placeholder {
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-base);
    padding: var(--padding-large);
    min-height: 200px;
  }

  .edit-controls {
    height: 600px;
    max-height: 600px;
    overflow: auto;
  }

  .edit-controls-html {
    height: 600px;
    max-height: 600px;
    overflow: auto;
  }

  .edit-controls-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
